<template>
  <div>
    <div v-if="successMessage" class="alert alert-success" style="color: rgb(5, 20, 48)">
      {{ successMessage }}
    </div>
    <div v-if="errorMessage" class="alert alert-danger" style="color: red;">
      {{ errorMessage }}
    </div>

    <b-card>
      <b-row>
        <div class="col-12 mt-16">
          <b-form @submit.prevent="addStakeholder" v-if="show">
            <!-- Heading -->
            <div style="background-color: rgb(97, 116, 152); height: 32px; border-radius: 4px;">
              <h4 style="color: rgb(223, 227, 238); margin-left: 5px; font-weight: bold;">
                Stakeholder Information
              </h4>
            </div>

            <div class="row">
              <!-- Name -->
              <div class="col-md-4 col-12">
                <b-form-group label="Name:" label-for="name">
                  <b-form-input id="name" placeholder="Enter name" :state="nameState" v-model="name" required />
                </b-form-group>
              </div>

              <!-- Email -->
              <div class="col-md-4 col-12">
                <b-form-group label="Email:" label-for="email">
                  <b-form-input id="email" type="email" placeholder="Enter email" :state="emailState" v-model="email" />
                </b-form-group>
              </div>

              <!-- Phone -->
              <div class="col-md-4 col-12">
                <b-form-group label="Phone Number:" label-for="phone">
                  <b-form-input id="phone" placeholder="Enter phone number" :state="phoneState" v-model="phone" />
                </b-form-group>
              </div>

              <!-- Address -->
              <div class="col-md-4 col-12">
                <b-form-group label="Address:" label-for="address">
                  <b-form-input id="address" placeholder="Enter address" :state="addressState" v-model="address" />
                </b-form-group>
              </div>

              <!-- Type -->
              <div class="col-md-4 col-12">
                <b-form-group label="Type:" label-for="type">
                  <b-form-select id="type" :state="typeState" v-model="type" :options="types" required />
                </b-form-group>
              </div>

              <!-- Share Percentage -->
              <div class="col-md-4 col-12">
                <b-form-group label="Share Percentage (%):" label-for="share_percentage">
                  <b-form-input
                    id="share_percentage"
                    type="number"
                    step="0.01"
                    placeholder="e.g. 25.50" :state="sharePercentageState"
                    v-model="share_percentage"
                  />
                </b-form-group>
              </div>

              <!-- Legal Document -->
              <b-col cols="12" md="6">
                <b-form-group
                  label="Legal Document:"
                  description="Upload PDF or DOCX (Max 5MB)"
                  label-class="font-weight-bold"
                >
                  <div
                    class="border rounded p-3 text-center"
                    style="cursor: pointer; background: #f9f9f9;"
                    @drop.prevent="onLegalDrop"
                    @dragover.prevent
                    @click="$refs.legalFileInput.click()"
                  >
                    <div v-if="!legalPreviewName">
                      <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                      <p class="mb-0">Drag & Drop or Click to Upload</p>
                    </div>
                    <div v-else>
                      <i class="fas fa-file-alt fa-2x text-primary"></i>
                      <p class="mb-0">{{ legalPreviewName }}</p>
                    </div>
                    <input
                      ref="legalFileInput"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      class="d-none"
                      @change="onLegalFileChange"
                    />
                  </div>
                  <small class="text-muted">
                    Only PDF/DOC/DOCX, Max size: 5MB
                  </small>
                  <div v-if="legalError" class="text-danger mt-1">
                    {{ legalError }}
                  </div>
                </b-form-group>
              </b-col>

              <!-- Profile Picture -->
              <b-col cols="12" md="6">
                <b-form-group
                  label="Profile Picture:"
                  description="Recommended size: 300x300px (JPG/PNG)"
                  label-class="font-weight-bold"
                >
                  <div
                    class="border rounded p-3 text-center"
                    style="cursor: pointer; background: #f9f9f9;"
                    @drop.prevent="onProfileDrop"
                    @dragover.prevent
                    @click="$refs.profileFileInput.click()"
                  >
                    <div v-if="!profilePreview">
                      <i class="fas fa-cloud-upload-alt fa-2x text-muted"></i>
                      <p class="mb-0">Drag & Drop or Click to Upload</p>
                    </div>
                    <div v-else>
                      <img
                        :src="profilePreview"
                        alt="Preview"
                        class="img-thumbnail"
                        style="max-height: 120px;"
                      />
                    </div>
                    <input
                      ref="profileFileInput"
                      type="file"
                      accept="image/png, image/jpeg"
                      class="d-none"
                      @change="onProfileFileChange"
                    />
                  </div>
                  <small class="text-muted">
                    Only JPG/PNG, Max size: 2MB
                  </small>
                  <div v-if="profileError" class="text-danger mt-1">
                    {{ profileError }}
                  </div>
                </b-form-group>
              </b-col>

              <!-- Notes -->
              <div class="col-md-12 col-12">
                <b-form-group label="Notes:" label-for="notes">
                  <b-form-textarea id="notes" placeholder="Additional notes" :state="notesState" v-model="notes" rows="3" />
                </b-form-group>
              </div>
            </div>

            <!-- Submit -->
            <b-button type="submit" variant="primary" class="mb-8 mr-8" :disabled="isLoading">
              <span v-if="!isLoading">Submit</span>
              <b-spinner v-else small></b-spinner>
            </b-button>
          </b-form>
        </div>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BSpinner,
} from "bootstrap-vue";
import axios from "../../../axios";
import imageCompression from "browser-image-compression";

export default {
  data() {
    return {
      show: true,
      isLoading: false,
      successMessage: "",
      errorMessage: "",

      // Stakeholder fields
      name: "",
      email: "",
      phone: "",
      address: "",
      type: "investor",
      share_percentage: "",
      notes: "",

      // File handling
      legalFile: null,
      legalPreviewName: null,
      legalError: "",

      profileFile: null,
      profilePreview: null,
      profileError: "",

      types: [
        { value: "client", text: "Client" },
        { value: "vendor", text: "Vendor" },
        { value: "investor", text: "Investor" },
        { value: "partner", text: "Partner" },
        { value: "employee", text: "Employee" },
      ],
    };
  },
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BSpinner,
  },
    computed: {
      nameState() {
        return this.validateField(this.name);
      },
      emailState() {
        return this.validateField(this.email);
      },
      phoneState() {
        return this.validateField(this.phone);
      },
      addressState() {
        return this.validateField(this.address);
      },
      typeState() {
        return this.validateField(this.type);
      },
      sharePercentageState() {
        return this.validateField(this.share_percentage);
      },
      notesState() {
        return this.validateField(this.notes);
      }
    },
  methods: {
     validateField(value) {
      if (value === null || value === undefined) return null;
      if (typeof value === "string") {
        return value.trim().length > 0 ? true : false;
      }
      return value ? true : false;
    },
    async addStakeholder() {
      this.isLoading = true;
      const formData = new FormData();
      formData.append("name", this.name);
      formData.append("email", this.email);
      formData.append("phone", this.phone);
      formData.append("address", this.address);
      formData.append("type", this.type);
      formData.append("share_percentage", this.share_percentage);
      formData.append("notes", this.notes);

      if (this.legalFile) formData.append("legal_document", this.legalFile);
      if (this.profileFile) formData.append("image", this.profileFile);

      try {
        await axios.post("stakeholdersStore", formData);
        this.$bvToast.toast("Stakeholder added successfully!", {
          title: "Success",
          variant: "primary",
          solid: true,
          toaster: "b-toaster-top-right",
          autoHideDelay: 5000,
        });
        this.isLoading = false;
        this.resetForm();
      } catch (error) {
        this.isLoading = false;
        this.$bvToast.toast(
          error.response?.data?.message || "An error occurred. Please try again.",
          {
            title: "Error",
            variant: "danger",
            solid: true,
            toaster: "b-toaster-top-right",
            autoHideDelay: 5000,
          }
        );
      }
    },

    resetForm() {
      this.name = "";
      this.email = "";
      this.phone = "";
      this.address = "";
      this.type = "investor";
      this.share_percentage = "";
      this.notes = "";
      this.legalFile = null;
      this.legalPreviewName = null;
      this.legalError = "";
      this.profileFile = null;
      this.profilePreview = null;
      this.profileError = "";
    },

    // LEGAL FILE
    async onLegalFileChange(e) {
      const file = e.target.files[0];
      await this.handleLegalFile(file);
    },
    async onLegalDrop(e) {
      const file = e.dataTransfer.files[0];
      await this.handleLegalFile(file);
    },
    async handleLegalFile(file) {
      if (!file) return;
      if (
        ![
          "application/pdf",
          "application/msword",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ].includes(file.type)
      ) {
        this.legalError = "Only PDF/DOC/DOCX allowed.";
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        this.legalError = "Max size is 5MB.";
        return;
      }
      this.legalFile = file;
      this.legalPreviewName = file.name;
      this.legalError = "";
    },

    // PROFILE PICTURE
    async onProfileFileChange(e) {
      const file = e.target.files[0];
      await this.handleProfileFile(file);
    },
    async onProfileDrop(e) {
      const file = e.dataTransfer.files[0];
      await this.handleProfileFile(file);
    },
    async handleProfileFile(file) {
      if (!file) return;
      if (!["image/jpeg", "image/png"].includes(file.type)) {
        this.profileError = "Only JPG/PNG allowed.";
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        this.profileError = "Max size is 2MB.";
        return;
      }

      try {
        const options = { maxSizeMB: 1, maxWidthOrHeight: 600, useWebWorker: true };
        const compressedFile = await imageCompression(file, options);
        this.profileFile = compressedFile;
        this.profilePreview = URL.createObjectURL(compressedFile);
        this.profileError = "";
      } catch (err) {
        this.profileFile = file;
        this.profilePreview = URL.createObjectURL(file);
      }
    },
  },
};
</script>
<style>
/* Make all Bootstrap-Vue form-group labels bold */
.b-form-group > label {
  font-weight: bold !important;
}
</style>